import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience, IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { useEffect, useMemo, useState } from 'react';
import { collectAllIssues } from './collectors';
import { GroupedIssue, groupIssues } from './group';
import { actorFullNameResolver, useResolveGroupActorNames } from './resolveGroupActorNames';
import { Issue, IssueCopyContext } from './types';

/** Builds issues → groups them → resolves real actor names for the groups
 * shown, then hands every occurrence an enriched `ctx` carrying the
 * resolver. `ctx`'s identity doesn't need to survive across re-renders —
 * every issue in a render shares the same enriched object, which is all
 * `personLabel` needs. */
export const useGroupedIssues = (
  tpl: Doc<IExperienceTemplate>,
  instances: Doc<IExperience>[],
  org: IOrganization,
  baseCtx: IssueCopyContext
): { groups: GroupedIssue[]; loading: boolean } => {
  const [issues, setIssues] = useState<Issue[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Deliberately not resetting `issues` to null here: a live Firestore
    // snapshot (e.g. right after a retry/ignore) re-runs this effect, and
    // blanking first would flash the full-screen Loader over the list on
    // every refresh. Keeping the previous groups visible until the new
    // collection resolves means the Loader only shows on the first load.
    collectAllIssues(tpl, instances, org, baseCtx).then((result) => {
      if (!cancelled) setIssues(result);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpl, instances, org]);

  const rawGroups = useMemo(() => (issues ? groupIssues(issues) : []), [issues]);
  const occurrenceGroups = useMemo(() => rawGroups.map((g) => g.occurrences), [rawGroups]);
  const namesByKey = useResolveGroupActorNames(org.id, occurrenceGroups);

  const groups = useMemo(() => {
    if (!issues || !issues[0]) return rawGroups;
    const enrichedCtx: IssueCopyContext = {
      ...issues[0].ctx,
      resolveActorFullName: actorFullNameResolver(namesByKey)
    };
    return rawGroups.map((g) => ({
      ...g,
      occurrences: g.occurrences.map((issue) => ({ ...issue, ctx: enrichedCtx }))
    }));
  }, [rawGroups, namesByKey, issues]);

  return { groups, loading: issues === null };
};
